import { Icons } from '@cockpitai/components/atoms'
import { Typography } from '@cockpitai/components/atoms/Typography'
import {
  TAlertCondition,
  TAlertNotifyItem,
} from '@cockpitai/integration/notification/types'
import { arrowDirection } from '@cockpitai/integration/panels/types'
import { memo } from 'react'
import { twMerge } from 'tailwind-merge'

type TSectionListItemProps = {
  title: string
  description?: string
  refData?: string
  indicator?: {
    arrow: arrowDirection
  }
  target_month?: string
  value?: number
  condition?: TAlertCondition
  read?: boolean
  notify?: TAlertNotifyItem[]
  isNotification?: boolean
  isAlert?: boolean
  onClick(): void
  active?: boolean
}

export const SectionListItem = memo(
  ({
    title,
    description,
    indicator,
    refData,
    target_month,
    condition,
    notify,
    value,
    read,
    isNotification = false,
    isAlert = false,
    onClick,
    active,
  }: TSectionListItemProps) => {
    const mapConditionToLabel = (condition: TAlertCondition) => {
      switch (condition) {
        case 'bigger':
          return 'Maior'

        case 'bigger-than':
          return 'Maior que'

        case 'equal':
          return 'Igual a'

        case 'less':
          return 'Menor'

        case 'less-than':
          return 'Menor que'

        default:
          return 'Condição desconhecida'
      }
    }

    const renderNotifyItems = () => {
      if (!notify || notify.length === 0) return ''

      const maxVisible = 4

      const visibleItems = notify.slice(0, maxVisible)

      const remainingCount = notify.length - maxVisible

      const formattedItems = visibleItems
        .map((item) => (typeof item === 'string' ? item : item.name))
        .join(', ')

      return remainingCount > 0
        ? `${formattedItems}, +${remainingCount}`
        : formattedItems
    }

    const renderContent = () => (
      <div className="relative">
        <div>
          <Typography
            type="bodyTwo"
            className="truncate text-left text-white/50">
            {title}
          </Typography>
          <Typography type="bodyThree" className="pr-2 text-left text-white/50">
            {description}
          </Typography>
          <Typography
            type="bodyThree"
            className="truncate text-left text-white/50">
            {refData}
          </Typography>
          <button
            type="button"
            onClick={onClick}
            className="ml-auto mt-1 flex items-center gap-2 rounded p-2 text-xs text-gray-400 transition-all duration-100 ease-in hover:bg-gray-800">
            Marcar como lida
          </button>
        </div>
        {read && <span className="isRead-bullet" />}
      </div>
    )

    const renderAlertContent = () => (
      <>
        <Typography type="bodyTwo" className="truncate text-left text-gray-100">
          {title}
        </Typography>
        <Typography
          type="bodyThree"
          className="truncate text-left text-gray-400">
          Em: {target_month} |{' '}
          {mapConditionToLabel(condition as TAlertCondition)}: {value}
        </Typography>
        <Typography
          type="bodyThree"
          className="truncate text-left text-gray-400">
          Notificar: {renderNotifyItems()}
        </Typography>

        <Typography
          type="bodyThree"
          className="truncate pt-1 text-left text-gray-400">
          {refData}
        </Typography>
        <button
          type="button"
          onClick={onClick}
          className="ml-auto mt-1 flex items-center gap-2 rounded p-2 text-xs text-gray-400 transition-all duration-100 ease-in hover:bg-gray-800">
          Excluir alerta
        </button>
      </>
    )

    if (isNotification) {
      return (
        <div className="flex flex-wrap bg-solid p-2">
          {indicator && (
            <div className="w-1/5">
              <div
                className={`flex h-12 w-12  items-center justify-center rounded-full ${
                  indicator.arrow === 'up'
                    ? 'bg-success-900 text-success-500'
                    : 'bg-warning-900 text-primary-500'
                }`}>
                {indicator.arrow === 'up' ? (
                  <Icons.ArrowUp className="text-inherit" />
                ) : (
                  <Icons.ArrowDown className="text-inherit" />
                )}
              </div>
            </div>
          )}
          <div className="w-4/5">{renderContent()}</div>
        </div>
      )
    }

    if (isAlert) {
      return (
        <div className="bg-solid p-2">
          <div>{renderAlertContent()}</div>
        </div>
      )
    }

    const handleClick = () => {
      if (!active) {
        onClick()
      }
    }

    return (
      <button
        type="button"
        onClick={handleClick}
        className={twMerge(
          'flex h-9 w-full items-center gap-2 px-2 transition-all duration-200 ease-in hover:bg-gray-700',
          active && 'pointer-events-none bg-gray-800',
        )}>
        <Icons.Chat style={{ width: '20px' }} className="text-white/50" />
        <Typography
          type="bodyOne"
          className="w-64 truncate text-left text-white/50">
          {title}
        </Typography>
      </button>
    )
  },
)
